/**
 * Market Appraisal Confirmation Dialog
 * 
 * Shows all inputs for the market appraisal report, categorized by:
 * - Auto-filled (from property/system)
 * - Extracted from RP data (CMA/Valuation)
 * - Manual agent inputs (editable)
 * 
 * Allows agent to review, edit, and confirm before HTML/PDF generation.
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Divider,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Checkbox
} from '@mui/material';
import {
  ExpandMore,
  Edit,
  CheckCircle,
  Info,
  AutoAwesome,
  Description,
  Refresh,
  Save,
  Preview,
  Close
} from '@mui/icons-material';
import api from '../services/api';

// Re-export types from types file for convenience
export type { ComparableSaleInput, MarketAppraisalInputs } from '../types/marketAppraisal';

interface MarketAppraisalConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (inputs: MarketAppraisalInputs) => void;
  onSave?: (inputs: MarketAppraisalInputs) => void; // Optional save callback
  initialInputs: MarketAppraisalInputs;
  loading?: boolean;
  agencySlug?: string;
  propertyId?: string;
}

const MarketAppraisalConfirmDialog: React.FC<MarketAppraisalConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  onSave,
  initialInputs,
  loading = false,
  agencySlug,
  propertyId
}) => {
  const [inputs, setInputs] = useState<MarketAppraisalInputs>(initialInputs);
  const [expandedSections, setExpandedSections] = useState<string[]>(['extracted', 'pricing', 'market', 'presentation']);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [extractionLimitReached, setExtractionLimitReached] = useState(false);
  const [extractionLimitMessage, setExtractionLimitMessage] = useState<string | null>(null);
  const [overLimitOption, setOverLimitOption] = useState<{ costCents: number; costDisplay: string; message: string } | null>(null);
  const [payConfirmOpen, setPayConfirmOpen] = useState(false);
  const [payConfirmOption, setPayConfirmOption] = useState<{ costDisplay: string; message: string } | null>(null);
  const params = useParams<{ agencySlug: string; propertyId: string }>();
  const effectiveAgencySlug = agencySlug || params.agencySlug;
  const effectivePropertyId = propertyId || params.propertyId;

  useEffect(() => {
    // Always update inputs when initialInputs changes (e.g., when dialog reopens with saved data)
    // Use deep copy to ensure React detects the change even if object reference is the same
    if (initialInputs) {
      setInputs(JSON.parse(JSON.stringify(initialInputs)));
    }
  }, [initialInputs]);

  useEffect(() => {
    if (!open || !effectiveAgencySlug || !effectivePropertyId) return;
    api.get(`/agencies/${effectiveAgencySlug}/properties/${effectivePropertyId}/consultation-report/extraction-limit`)
      .then((res) => {
        const { allowed, message, overLimitOption: opt } = res.data || {};
        setExtractionLimitReached(!allowed);
        setExtractionLimitMessage(message || null);
        setOverLimitOption(opt || null);
      })
      .catch(() => {
        setExtractionLimitReached(false);
        setExtractionLimitMessage(null);
        setOverLimitOption(null);
      });
  }, [open, effectiveAgencySlug, effectivePropertyId]);

  const handleSectionToggle = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const updateManualInput = <K extends keyof MarketAppraisalInputs['manualInputs']>(
    section: K,
    field: keyof MarketAppraisalInputs['manualInputs'][K],
    value: any
  ) => {
    setInputs(prev => ({
      ...prev,
      manualInputs: {
        ...prev.manualInputs,
        [section]: {
          ...prev.manualInputs[section],
          [field]: value
        }
      }
    }));
  };

  const updateArrayField = <K extends keyof MarketAppraisalInputs['manualInputs']>(
    section: K,
    field: keyof MarketAppraisalInputs['manualInputs'][K],
    index: number,
    value: any
  ) => {
    setInputs(prev => {
      const sectionData = prev.manualInputs[section] as any;
      const arrayField = sectionData[field] as any[];
      const updated = [...arrayField];
      updated[index] = value;
      return {
        ...prev,
        manualInputs: {
          ...prev.manualInputs,
          [section]: {
            ...sectionData,
            [field]: updated
          }
        }
      };
    });
  };

  const addArrayItem = <K extends keyof MarketAppraisalInputs['manualInputs']>(
    section: K,
    field: keyof MarketAppraisalInputs['manualInputs'][K],
    defaultValue: any
  ) => {
    setInputs(prev => {
      const sectionData = prev.manualInputs[section] as any;
      const arrayField = sectionData[field] as any[];
      return {
        ...prev,
        manualInputs: {
          ...prev.manualInputs,
          [section]: {
            ...sectionData,
            [field]: [...arrayField, defaultValue]
          }
        }
      };
    });
  };

  const removeArrayItem = <K extends keyof MarketAppraisalInputs['manualInputs']>(
    section: K,
    field: keyof MarketAppraisalInputs['manualInputs'][K],
    index: number
  ) => {
    setInputs(prev => {
      const sectionData = prev.manualInputs[section] as any;
      const arrayField = sectionData[field] as any[];
      const updated = arrayField.filter((_, i) => i !== index);
      return {
        ...prev,
        manualInputs: {
          ...prev.manualInputs,
          [section]: {
            ...sectionData,
            [field]: updated
          }
        }
      };
    });
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Description />
          <Typography variant="h6">Review & Confirm Market Appraisal Data</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Review all data before generating the PDF. Edit manual inputs as needed. Parts of this data were
          initially generated with AI and may contain inaccuracies or outdated assumptions — please verify
          figures, wording, and recommendations carefully before finalizing and sending to the vendor.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          
          {/* Auto-filled Data (Read-only) */}
          <Accordion expanded={expandedSections.includes('auto-filled')} onChange={() => handleSectionToggle('auto-filled')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Info color="info" />
                <Typography variant="subtitle1" sx={{ flex: 1 }}>
                  Auto-filled Data (from Property & System)
                </Typography>
                <Chip label="Read-only" size="small" color="info" variant="outlined" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Property</Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow><TableCell><strong>Address</strong></TableCell><TableCell>{inputs.property.address}</TableCell></TableRow>
                      <TableRow><TableCell><strong>Type</strong></TableCell><TableCell>{inputs.property.type}</TableCell></TableRow>
                      <TableRow><TableCell><strong>Bedrooms</strong></TableCell><TableCell>{inputs.property.bedrooms}</TableCell></TableRow>
                      <TableRow><TableCell><strong>Bathrooms</strong></TableCell><TableCell>{inputs.property.bathrooms}</TableCell></TableRow>
                      <TableRow><TableCell><strong>Car Spaces</strong></TableCell><TableCell>{inputs.property.carSpaces}</TableCell></TableRow>
                      {inputs.property.landSizeSqm && <TableRow><TableCell><strong>Land Size</strong></TableCell><TableCell>{inputs.property.landSizeSqm} m²</TableCell></TableRow>}
                      {inputs.property.internalSizeSqm && <TableRow><TableCell><strong>Internal Size</strong></TableCell><TableCell>{inputs.property.internalSizeSqm} m²</TableCell></TableRow>}
                      {inputs.property.aspect && <TableRow><TableCell><strong>Aspect</strong></TableCell><TableCell>{inputs.property.aspect}</TableCell></TableRow>}
                      {inputs.property.titleType && <TableRow><TableCell><strong>Title Type</strong></TableCell><TableCell>{inputs.property.titleType}</TableCell></TableRow>}
                      {inputs.property.zoning && <TableRow><TableCell><strong>Zoning</strong></TableCell><TableCell>{inputs.property.zoning}</TableCell></TableRow>}
                      {inputs.property.yearBuilt && <TableRow><TableCell><strong>Year Built</strong></TableCell><TableCell>{inputs.property.yearBuilt}</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Agency & Agent</Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow><TableCell><strong>Agency</strong></TableCell><TableCell>{inputs.agency.name}</TableCell></TableRow>
                      <TableRow><TableCell><strong>Agent</strong></TableCell><TableCell>{inputs.agent.name}</TableCell></TableRow>
                      <TableRow><TableCell><strong>Licence No</strong></TableCell><TableCell>{inputs.agent.licenceNo}</TableCell></TableRow>
                      <TableRow><TableCell><strong>Vendor</strong></TableCell><TableCell>{inputs.vendor.name}</TableCell></TableRow>
                      <TableRow><TableCell><strong>Appraisal Date</strong></TableCell><TableCell>{inputs.appraisal.date}</TableCell></TableRow>
                      <TableRow><TableCell><strong>Version</strong></TableCell><TableCell>{inputs.appraisal.version}</TableCell></TableRow>
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Extracted from RP Data (Editable) */}
          <Accordion expanded={expandedSections.includes('extracted')} onChange={() => handleSectionToggle('extracted')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <AutoAwesome color="secondary" />
                <Typography variant="subtitle1" sx={{ flex: 1 }}>
                  Extracted from RP Data (CMA & Valuation Reports)
                </Typography>
                <Chip label="Editable" size="small" color="secondary" variant="outlined" />
                <Chip 
                  label={`${inputs.extractedData.comparables.length} comparables`} 
                  size="small" 
                  color="secondary" 
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Re-extract button - disabled when daily extraction limit reached */}
                {extractionLimitReached && extractionLimitMessage && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {extractionLimitMessage} You can still enter comparable sales manually below and generate the report.
                  </Alert>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Refresh />}
                    onClick={async () => {
                      if (!effectiveAgencySlug || !effectivePropertyId) return;
                      setExtracting(true);
                      try {
                        const response = await api.post(`/agencies/${effectiveAgencySlug}/properties/${effectivePropertyId}/consultation-report/extract-rp-data`);
                        const extracted = response.data.extractedData || response.data;
                        setInputs(prev => ({
                          ...prev,
                          extractedData: {
                            comparables: extracted.comparables || [],
                            marketStatistics: extracted.marketStatistics || {
                              medianPrice: null,
                              movement12m: null,
                              avgDom: null,
                              clearanceRate: null
                            }
                          }
                        }));
                      } catch (err: any) {
                        console.error('Error extracting RP data:', err);
                        const data = err.response?.data || {};
                        const isLimitReached = err.response?.status === 429 && data.extractionLimitReached;
                        const overOpt = data.overLimitOption;
                        if (isLimitReached) {
                          setExtractionLimitReached(true);
                          setExtractionLimitMessage(data.error || 'Daily limit reached for Extract RP Data.');
                          if (overOpt) {
                            setPayConfirmOption({ costDisplay: overOpt.costDisplay || '$0.98', message: overOpt.message || 'Use 1 more AI extraction from your agency credits?' });
                            setPayConfirmOpen(true);
                          }
                        } else if (err.response?.status === 402 && data.insufficientCredits) {
                          alert(data.error || 'Insufficient agency credits.');
                        } else {
                          alert(data.error || 'Failed to extract RP data');
                        }
                      } finally {
                        setExtracting(false);
                      }
                    }}
                    disabled={extracting}
                  >
                    {extracting ? 'Extracting...' : 'Re-extract RP Data'}
                  </Button>
                  {extractionLimitReached && overLimitOption && (
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={() => {
                        setPayConfirmOption({ costDisplay: overLimitOption.costDisplay, message: overLimitOption.message });
                        setPayConfirmOpen(true);
                      }}
                      disabled={extracting}
                    >
                      Use 1 extraction for {overLimitOption.costDisplay} (credits)
                    </Button>
                  )}
                </Box>
                <Dialog open={payConfirmOpen} onClose={() => setPayConfirmOpen(false)} maxWidth="sm" fullWidth>
                  <DialogTitle>Confirm charge</DialogTitle>
                  <DialogContent>
                    {payConfirmOption && (
                      <Typography>
                        {payConfirmOption.message} This will deduct {payConfirmOption.costDisplay} from your agency credits.
                      </Typography>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setPayConfirmOpen(false)}>Cancel</Button>
                    <Button
                      variant="contained"
                      onClick={async () => {
                        if (!effectiveAgencySlug || !effectivePropertyId) return;
                        setExtracting(true);
                        try {
                          const response = await api.post(
                            `/agencies/${effectiveAgencySlug}/properties/${effectivePropertyId}/consultation-report/extract-rp-data`,
                            { confirmCharge: true }
                          );
                          const extracted = response.data.extractedData || response.data;
                          setInputs(prev => ({
                            ...prev,
                            extractedData: {
                              comparables: extracted.comparables || [],
                              marketStatistics: extracted.marketStatistics || {
                                medianPrice: null,
                                movement12m: null,
                                avgDom: null,
                                clearanceRate: null
                              }
                            }
                          }));
                          setPayConfirmOpen(false);
                        } catch (err: any) {
                          const data = err.response?.data || {};
                          if (err.response?.status === 402 && data.insufficientCredits) {
                            alert(data.error || 'Insufficient agency credits.');
                          } else {
                            alert(data.error || 'Failed to extract RP data');
                          }
                        } finally {
                          setExtracting(false);
                        }
                      }}
                      disabled={extracting}
                    >
                      {extracting ? 'Extracting...' : 'Confirm and pay'}
                    </Button>
                  </DialogActions>
                </Dialog>
                
                {/* Info message if no data */}
                {(!inputs.extractedData.comparables.length && 
                  !inputs.extractedData.marketStatistics.medianPrice && 
                  !inputs.extractedData.marketStatistics.movement12m && 
                  !inputs.extractedData.marketStatistics.avgDom && 
                  !inputs.extractedData.marketStatistics.clearanceRate) && (
                  <Alert severity="info">
                    {extractionLimitReached
                      ? 'You can enter comparable sales manually in the table below and generate the report.'
                      : 'No extracted data found. Click "Re-extract RP Data" above to extract data from CMA and Valuation reports.'}
                  </Alert>
                )}
                
                {/* Market Statistics */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Market Statistics (from Valuation Report)</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                      label="Median Price"
                      value={inputs.extractedData.marketStatistics.medianPrice || ''}
                      onChange={(e) => {
                        setInputs(prev => ({
                          ...prev,
                          extractedData: {
                            ...prev.extractedData,
                            marketStatistics: {
                              ...prev.extractedData.marketStatistics,
                              medianPrice: e.target.value
                            }
                          }
                        }));
                      }}
                      fullWidth
                      size="small"
                      placeholder="e.g., $2,595,318"
                    />
                    <TextField
                      label="12-Month Movement"
                      value={inputs.extractedData.marketStatistics.movement12m || ''}
                      onChange={(e) => {
                        setInputs(prev => ({
                          ...prev,
                          extractedData: {
                            ...prev.extractedData,
                            marketStatistics: {
                              ...prev.extractedData.marketStatistics,
                              movement12m: e.target.value
                            }
                          }
                        }));
                      }}
                      fullWidth
                      size="small"
                      placeholder="e.g., +6.85%"
                    />
                    <TextField
                      label="Avg Days on Market"
                      value={inputs.extractedData.marketStatistics.avgDom || ''}
                      onChange={(e) => {
                        setInputs(prev => ({
                          ...prev,
                          extractedData: {
                            ...prev.extractedData,
                            marketStatistics: {
                              ...prev.extractedData.marketStatistics,
                              avgDom: e.target.value
                            }
                          }
                        }));
                      }}
                      fullWidth
                      size="small"
                      placeholder="e.g., 25 days"
                    />
                    <TextField
                      label="Auction Clearance Rate"
                      value={inputs.extractedData.marketStatistics.clearanceRate || ''}
                      onChange={(e) => {
                        setInputs(prev => ({
                          ...prev,
                          extractedData: {
                            ...prev.extractedData,
                            marketStatistics: {
                              ...prev.extractedData.marketStatistics,
                              clearanceRate: e.target.value
                            }
                          }
                        }));
                      }}
                      fullWidth
                      size="small"
                      placeholder="e.g., 75%"
                    />
                  </Box>
                </Box>

                {/* Comparable Sales */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      Comparable Sales (from CMA Report) - {inputs.extractedData.comparables.length} found
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {inputs.extractedData.comparables.filter(c => c.selected !== false).length} selected
                    </Typography>
                  </Box>
                  {inputs.extractedData.comparables.length === 0 ? (
                    <Alert severity="warning">
                      No comparable sales extracted from CMA report.
                    </Alert>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {inputs.extractedData.comparables.map((comp, idx) => (
                        <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Checkbox
                              checked={comp.selected !== false}
                              onChange={(e) => {
                                const updatedComparables = [...inputs.extractedData.comparables];
                                updatedComparables[idx] = { ...comp, selected: e.target.checked };
                                setInputs(prev => ({
                                  ...prev,
                                  extractedData: {
                                    ...prev.extractedData,
                                    comparables: updatedComparables
                                  }
                                }));
                              }}
                              size="small"
                            />
                            <Typography variant="subtitle2" sx={{ flex: 1 }}>
                              Comparable Sale #{idx + 1}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const updatedComparables = inputs.extractedData.comparables.filter((_, i) => i !== idx);
                                setInputs(prev => ({
                                  ...prev,
                                  extractedData: {
                                    ...prev.extractedData,
                                    comparables: updatedComparables
                                  }
                                }));
                              }}
                            >
                              ×
                            </IconButton>
                          </Box>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <TextField
                              label="Address"
                              value={comp.address || ''}
                              onChange={(e) => {
                                const updatedComparables = [...inputs.extractedData.comparables];
                                updatedComparables[idx] = { ...comp, address: e.target.value };
                                setInputs(prev => ({
                                  ...prev,
                                  extractedData: {
                                    ...prev.extractedData,
                                    comparables: updatedComparables
                                  }
                                }));
                              }}
                              fullWidth
                              size="small"
                            />
                            <TextField
                              label="Sale Price"
                              value={comp.salePrice || ''}
                              onChange={(e) => {
                                const updatedComparables = [...inputs.extractedData.comparables];
                                updatedComparables[idx] = { ...comp, salePrice: e.target.value };
                                setInputs(prev => ({
                                  ...prev,
                                  extractedData: {
                                    ...prev.extractedData,
                                    comparables: updatedComparables
                                  }
                                }));
                              }}
                              fullWidth
                              size="small"
                              placeholder="e.g., $3,200,000 or Undisclosed"
                            />
                            <TextField
                              label="Sold Date"
                              value={comp.soldDate || ''}
                              onChange={(e) => {
                                const updatedComparables = [...inputs.extractedData.comparables];
                                updatedComparables[idx] = { ...comp, soldDate: e.target.value };
                                setInputs(prev => ({
                                  ...prev,
                                  extractedData: {
                                    ...prev.extractedData,
                                    comparables: updatedComparables
                                  }
                                }));
                              }}
                              fullWidth
                              size="small"
                              placeholder="e.g., December 2025"
                            />
                            <TextField
                              label="Distance (km)"
                              value={comp.distanceKm || ''}
                              onChange={(e) => {
                                const updatedComparables = [...inputs.extractedData.comparables];
                                updatedComparables[idx] = { ...comp, distanceKm: e.target.value };
                                setInputs(prev => ({
                                  ...prev,
                                  extractedData: {
                                    ...prev.extractedData,
                                    comparables: updatedComparables
                                  }
                                }));
                              }}
                              fullWidth
                              size="small"
                              type="number"
                              inputProps={{ step: 0.1 }}
                            />
                            <TextField
                              label="Bedrooms"
                              value={comp.bedrooms || ''}
                              onChange={(e) => {
                                const updatedComparables = [...inputs.extractedData.comparables];
                                updatedComparables[idx] = { ...comp, bedrooms: e.target.value };
                                setInputs(prev => ({
                                  ...prev,
                                  extractedData: {
                                    ...prev.extractedData,
                                    comparables: updatedComparables
                                  }
                                }));
                              }}
                              fullWidth
                              size="small"
                              type="number"
                            />
                            <TextField
                              label="Bathrooms"
                              value={comp.bathrooms || ''}
                              onChange={(e) => {
                                const updatedComparables = [...inputs.extractedData.comparables];
                                updatedComparables[idx] = { ...comp, bathrooms: e.target.value };
                                setInputs(prev => ({
                                  ...prev,
                                  extractedData: {
                                    ...prev.extractedData,
                                    comparables: updatedComparables
                                  }
                                }));
                              }}
                              fullWidth
                              size="small"
                              type="number"
                            />
                            <TextField
                              label="Car Spaces"
                              value={comp.carSpaces || ''}
                              onChange={(e) => {
                                const updatedComparables = [...inputs.extractedData.comparables];
                                updatedComparables[idx] = { ...comp, carSpaces: e.target.value };
                                setInputs(prev => ({
                                  ...prev,
                                  extractedData: {
                                    ...prev.extractedData,
                                    comparables: updatedComparables
                                  }
                                }));
                              }}
                              fullWidth
                              size="small"
                              type="number"
                            />
                            <TextField
                              label="Land Size (m²)"
                              value={comp.landSizeSqm || ''}
                              onChange={(e) => {
                                const updatedComparables = [...inputs.extractedData.comparables];
                                updatedComparables[idx] = { ...comp, landSizeSqm: e.target.value };
                                setInputs(prev => ({
                                  ...prev,
                                  extractedData: {
                                    ...prev.extractedData,
                                    comparables: updatedComparables
                                  }
                                }));
                              }}
                              fullWidth
                              size="small"
                              type="number"
                            />
                            <TextField
                              label="Internal Size (m²)"
                              value={comp.internalSizeSqm || ''}
                              onChange={(e) => {
                                const updatedComparables = [...inputs.extractedData.comparables];
                                updatedComparables[idx] = { ...comp, internalSizeSqm: e.target.value };
                                setInputs(prev => ({
                                  ...prev,
                                  extractedData: {
                                    ...prev.extractedData,
                                    comparables: updatedComparables
                                  }
                                }));
                              }}
                              fullWidth
                              size="small"
                              type="number"
                            />
                            <TextField
                              label="Year Built"
                              value={comp.yearBuilt || ''}
                              onChange={(e) => {
                                const updatedComparables = [...inputs.extractedData.comparables];
                                updatedComparables[idx] = { ...comp, yearBuilt: e.target.value };
                                setInputs(prev => ({
                                  ...prev,
                                  extractedData: {
                                    ...prev.extractedData,
                                    comparables: updatedComparables
                                  }
                                }));
                              }}
                              fullWidth
                              size="small"
                              type="number"
                            />
                            <TextField
                              label="Days on Market"
                              value={comp.dom || ''}
                              onChange={(e) => {
                                const updatedComparables = [...inputs.extractedData.comparables];
                                updatedComparables[idx] = { ...comp, dom: e.target.value };
                                setInputs(prev => ({
                                  ...prev,
                                  extractedData: {
                                    ...prev.extractedData,
                                    comparables: updatedComparables
                                  }
                                }));
                              }}
                              fullWidth
                              size="small"
                              type="number"
                            />
                            <TextField
                              label="Notes"
                              value={comp.notes || ''}
                              onChange={(e) => {
                                const updatedComparables = [...inputs.extractedData.comparables];
                                updatedComparables[idx] = { ...comp, notes: e.target.value };
                                setInputs(prev => ({
                                  ...prev,
                                  extractedData: {
                                    ...prev.extractedData,
                                    comparables: updatedComparables
                                  }
                                }));
                              }}
                              fullWidth
                              size="small"
                              multiline
                              rows={2}
                              placeholder="Comparison notes..."
                            />
                          </Box>
                        </Paper>
                      ))}
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const newComparable: ComparableSaleInput = {
                            address: '',
                            salePrice: '',
                            soldDate: '',
                            distanceKm: '',
                            bedrooms: '',
                            bathrooms: '',
                            carSpaces: '',
                            selected: true
                          };
                          setInputs(prev => ({
                            ...prev,
                            extractedData: {
                              ...prev.extractedData,
                              comparables: [...prev.extractedData.comparables, newComparable]
                            }
                          }));
                        }}
                        sx={{ mt: 1 }}
                      >
                        + Add Comparable Sale
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Manual Inputs - Pricing */}
          <Accordion expanded={expandedSections.includes('pricing')} onChange={() => handleSectionToggle('pricing')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Edit color="primary" />
                <Typography variant="subtitle1" sx={{ flex: 1 }}>
                  Pricing & Market Range
                </Typography>
                <Chip label="Editable" size="small" color="primary" variant="outlined" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                <TextField
                  label="Price Range Low"
                  value={inputs.manualInputs.pricing.rangeLow}
                  onChange={(e) => updateManualInput('pricing', 'rangeLow', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Price Range High"
                  value={inputs.manualInputs.pricing.rangeHigh}
                  onChange={(e) => updateManualInput('pricing', 'rangeHigh', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Confidence Level"
                  value={inputs.manualInputs.pricing.confidence}
                  onChange={(e) => updateManualInput('pricing', 'confidence', e.target.value)}
                  fullWidth
                  size="small"
                  select
                  SelectProps={{ native: true }}
                >
                  <option value="High">High</option>
                  <option value="Moderate">Moderate</option>
                  <option value="Low">Low</option>
                </TextField>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Manual Inputs - Market Conditions */}
          <Accordion expanded={expandedSections.includes('market')} onChange={() => handleSectionToggle('market')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Edit color="primary" />
                <Typography variant="subtitle1" sx={{ flex: 1 }}>
                  Market Conditions
                </Typography>
                <Chip label="Editable" size="small" color="primary" variant="outlined" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                  <TextField
                    label="Buyer Demand"
                    value={inputs.manualInputs.market.demand}
                    onChange={(e) => updateManualInput('market', 'demand', e.target.value)}
                    fullWidth
                    size="small"
                    select
                    SelectProps={{ native: true }}
                  >
                    <option value="Strong">Strong</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Soft">Soft</option>
                  </TextField>
                  <TextField
                    label="Stock Level"
                    value={inputs.manualInputs.market.stock}
                    onChange={(e) => updateManualInput('market', 'stock', e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Momentum"
                    value={inputs.manualInputs.market.momentum}
                    onChange={(e) => updateManualInput('market', 'momentum', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Box>
                <TextField
                  label="Market Summary"
                  value={inputs.manualInputs.market.summary}
                  onChange={(e) => updateManualInput('market', 'summary', e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                />
                <TextField
                  label="Buyer Behaviour"
                  value={inputs.manualInputs.market.buyerBehaviour}
                  onChange={(e) => updateManualInput('market', 'buyerBehaviour', e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                />
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Key Demand Drivers</Typography>
                  {inputs.manualInputs.market.demandDrivers.map((driver, idx) => (
                    <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        value={driver}
                        onChange={(e) => updateArrayField('market', 'demandDrivers', idx, e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <IconButton onClick={() => removeArrayItem('market', 'demandDrivers', idx)} size="small">
                        ×
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    size="small"
                    onClick={() => addArrayItem('market', 'demandDrivers', '')}
                    sx={{ mt: 1 }}
                  >
                    + Add Driver
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Manual Inputs - Presentation */}
          <Accordion expanded={expandedSections.includes('presentation')} onChange={() => handleSectionToggle('presentation')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Edit color="primary" />
                <Typography variant="subtitle1" sx={{ flex: 1 }}>
                  Presentation & Buyer Appeal
                </Typography>
                <Chip label="Editable" size="small" color="primary" variant="outlined" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
                <TextField
                  label="Overall Condition"
                  value={inputs.manualInputs.presentation.condition}
                  onChange={(e) => updateManualInput('presentation', 'condition', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Buyer Appeal"
                  value={inputs.manualInputs.presentation.buyerAppeal}
                  onChange={(e) => updateManualInput('presentation', 'buyerAppeal', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Presentation Score"
                  value={inputs.manualInputs.presentation.score}
                  onChange={(e) => updateManualInput('presentation', 'score', e.target.value)}
                  fullWidth
                  size="small"
                />
              </Box>
              <TextField
                label="Agent Notes"
                value={inputs.manualInputs.presentation.notes}
                onChange={(e) => updateManualInput('presentation', 'notes', e.target.value)}
                fullWidth
                multiline
                rows={3}
                size="small"
              />
            </AccordionDetails>
          </Accordion>

          {/* Manual Inputs - Strategy */}
          <Accordion expanded={expandedSections.includes('strategy')} onChange={() => handleSectionToggle('strategy')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Edit color="primary" />
                <Typography variant="subtitle1" sx={{ flex: 1 }}>
                  Pricing & Sale Strategy
                </Typography>
                <Chip label="Editable" size="small" color="primary" variant="outlined" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Recommended Sale Method"
                  value={inputs.manualInputs.strategy.method}
                  onChange={(e) => updateManualInput('strategy', 'method', e.target.value)}
                  fullWidth
                  size="small"
                  select
                  SelectProps={{ native: true }}
                >
                  <option value="Private Treaty">Private Treaty</option>
                  <option value="Auction">Auction</option>
                  <option value="Expressions of Interest">Expressions of Interest</option>
                  <option value="Off-market">Off-market</option>
                </TextField>
                <TextField
                  label="Rationale"
                  value={inputs.manualInputs.strategy.rationale}
                  onChange={(e) => updateManualInput('strategy', 'rationale', e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                />
                <TextField
                  label="Pricing Approach"
                  value={inputs.manualInputs.strategy.pricingApproach}
                  onChange={(e) => updateManualInput('strategy', 'pricingApproach', e.target.value)}
                  fullWidth
                  size="small"
                />
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Risk Analysis</Typography>
                  {inputs.manualInputs.strategy.risks.map((risk, idx) => (
                    <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        value={risk}
                        onChange={(e) => updateArrayField('strategy', 'risks', idx, e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <IconButton onClick={() => removeArrayItem('strategy', 'risks', idx)} size="small">
                        ×
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    size="small"
                    onClick={() => addArrayItem('strategy', 'risks', '')}
                    sx={{ mt: 1 }}
                  >
                    + Add Risk
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Manual Inputs - Marketing */}
          <Accordion expanded={expandedSections.includes('marketing')} onChange={() => handleSectionToggle('marketing')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Edit color="primary" />
                <Typography variant="subtitle1" sx={{ flex: 1 }}>
                  Marketing Campaign
                </Typography>
                <Chip label="Editable" size="small" color="primary" variant="outlined" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Recommended Campaign Modules</Typography>
                  {inputs.manualInputs.marketing.modules.map((module, idx) => (
                    <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        value={module}
                        onChange={(e) => updateArrayField('marketing', 'modules', idx, e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <IconButton onClick={() => removeArrayItem('marketing', 'modules', idx)} size="small">
                        ×
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    size="small"
                    onClick={() => addArrayItem('marketing', 'modules', '')}
                    sx={{ mt: 1 }}
                  >
                    + Add Module
                  </Button>
                </Box>
                <TextField
                  label="Suggested Campaign Length"
                  value={inputs.manualInputs.marketing.duration}
                  onChange={(e) => updateManualInput('marketing', 'duration', e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Agent Notes"
                  value={inputs.manualInputs.marketing.notes}
                  onChange={(e) => updateManualInput('marketing', 'notes', e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Manual Inputs - Property Features */}
          <Accordion expanded={expandedSections.includes('features')} onChange={() => handleSectionToggle('features')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Edit color="primary" />
                <Typography variant="subtitle1" sx={{ flex: 1 }}>
                  Key Property Features
                </Typography>
                <Chip label="Editable" size="small" color="primary" variant="outlined" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {inputs.manualInputs.propertyFeatures.map((feature, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      value={feature}
                      onChange={(e) => {
                        const updated = [...inputs.manualInputs.propertyFeatures];
                        updated[idx] = e.target.value;
                        setInputs(prev => ({
                          ...prev,
                          manualInputs: {
                            ...prev.manualInputs,
                            propertyFeatures: updated
                          }
                        }));
                      }}
                      fullWidth
                      size="small"
                    />
                    <IconButton 
                      onClick={() => {
                        const updated = inputs.manualInputs.propertyFeatures.filter((_, i) => i !== idx);
                        setInputs(prev => ({
                          ...prev,
                          manualInputs: {
                            ...prev.manualInputs,
                            propertyFeatures: updated
                          }
                        }));
                      }} 
                      size="small"
                    >
                      ×
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  onClick={() => {
                    setInputs(prev => ({
                      ...prev,
                      manualInputs: {
                        ...prev.manualInputs,
                        propertyFeatures: [...prev.manualInputs.propertyFeatures, '']
                      }
                    }));
                  }}
                  sx={{ mt: 1 }}
                >
                  + Add Feature
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Manual Inputs - Competition */}
          <Accordion expanded={expandedSections.includes('competition')} onChange={() => handleSectionToggle('competition')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Edit color="primary" />
                <Typography variant="subtitle1" sx={{ flex: 1 }}>
                  Current Market Competition
                </Typography>
                <Chip label="Editable" size="small" color="primary" variant="outlined" />
                <Chip label={`${inputs.manualInputs.competition.length} listings`} size="small" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {inputs.manualInputs.competition.map((comp, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 1 }}>
                      <TextField
                        label="Address"
                        value={comp.address}
                        onChange={(e) => {
                          const updated = [...inputs.manualInputs.competition];
                          updated[idx] = { ...updated[idx], address: e.target.value };
                          setInputs(prev => ({
                            ...prev,
                            manualInputs: {
                              ...prev.manualInputs,
                              competition: updated
                            }
                          }));
                        }}
                        size="small"
                      />
                      <TextField
                        label="Price Guide"
                        value={comp.priceGuide}
                        onChange={(e) => {
                          const updated = [...inputs.manualInputs.competition];
                          updated[idx] = { ...updated[idx], priceGuide: e.target.value };
                          setInputs(prev => ({
                            ...prev,
                            manualInputs: {
                              ...prev.manualInputs,
                              competition: updated
                            }
                          }));
                        }}
                        size="small"
                      />
                      <TextField
                        label="DOM"
                        value={comp.dom}
                        onChange={(e) => {
                          const updated = [...inputs.manualInputs.competition];
                          updated[idx] = { ...updated[idx], dom: e.target.value };
                          setInputs(prev => ({
                            ...prev,
                            manualInputs: {
                              ...prev.manualInputs,
                              competition: updated
                            }
                          }));
                        }}
                        size="small"
                      />
                      <TextField
                        label="Notes"
                        value={comp.notes}
                        onChange={(e) => {
                          const updated = [...inputs.manualInputs.competition];
                          updated[idx] = { ...updated[idx], notes: e.target.value };
                          setInputs(prev => ({
                            ...prev,
                            manualInputs: {
                              ...prev.manualInputs,
                              competition: updated
                            }
                          }));
                        }}
                        size="small"
                      />
                      <IconButton
                        onClick={() => {
                          const updated = inputs.manualInputs.competition.filter((_, i) => i !== idx);
                          setInputs(prev => ({
                            ...prev,
                            manualInputs: {
                              ...prev.manualInputs,
                              competition: updated
                            }
                          }));
                        }}
                        size="small"
                      >
                        ×
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
                <Button
                  size="small"
                  onClick={() => addArrayItem('competition', 'competition', { address: '', priceGuide: '', dom: '', notes: '' })}
                >
                  + Add Competition Listing
                </Button>
                <TextField
                  label="Positioning Insight (optional)"
                  value={inputs.manualInputs.competitionInsight || ''}
                  onChange={(e) => {
                    setInputs(prev => ({
                      ...prev,
                      manualInputs: {
                        ...prev.manualInputs,
                        competitionInsight: e.target.value
                      }
                    }));
                  }}
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Manual Inputs - Agent Stats & Recent Sales */}
          <Accordion expanded={expandedSections.includes('agent')} onChange={() => handleSectionToggle('agent')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Edit color="primary" />
                <Typography variant="subtitle1" sx={{ flex: 1 }}>
                  Agent Expertise & Recent Sales
                </Typography>
                <Chip label="Editable" size="small" color="primary" variant="outlined" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Agent Statistics</Typography>
                  {inputs.manualInputs.agentStats.map((stat, idx) => (
                    <Box key={idx} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 1, mb: 1 }}>
                      <TextField
                        label="Label"
                        value={stat.label}
                        onChange={(e) => {
                          const updated = [...inputs.manualInputs.agentStats];
                          updated[idx] = { ...updated[idx], label: e.target.value };
                          setInputs(prev => ({
                            ...prev,
                            manualInputs: {
                              ...prev.manualInputs,
                              agentStats: updated
                            }
                          }));
                        }}
                        size="small"
                      />
                      <TextField
                        label="Value"
                        value={stat.value}
                        onChange={(e) => {
                          const updated = [...inputs.manualInputs.agentStats];
                          updated[idx] = { ...updated[idx], value: e.target.value };
                          setInputs(prev => ({
                            ...prev,
                            manualInputs: {
                              ...prev.manualInputs,
                              agentStats: updated
                            }
                          }));
                        }}
                        size="small"
                      />
                      <IconButton
                        onClick={() => {
                          const updated = inputs.manualInputs.agentStats.filter((_, i) => i !== idx);
                          setInputs(prev => ({
                            ...prev,
                            manualInputs: {
                              ...prev.manualInputs,
                              agentStats: updated
                            }
                          }));
                        }}
                        size="small"
                      >
                        ×
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    size="small"
                    onClick={() => addArrayItem('agentStats', 'agentStats', { label: '', value: '' })}
                    sx={{ mt: 1 }}
                  >
                    + Add Statistic
                  </Button>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Recent Local Sales</Typography>
                  {inputs.manualInputs.recentSales.map((sale, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 1 }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 1 }}>
                        <TextField
                          label="Address"
                          value={sale.address}
                          onChange={(e) => {
                            const updated = [...inputs.manualInputs.recentSales];
                            updated[idx] = { ...updated[idx], address: e.target.value };
                            setInputs(prev => ({
                              ...prev,
                              manualInputs: {
                                ...prev.manualInputs,
                                recentSales: updated
                              }
                            }));
                          }}
                          size="small"
                        />
                        <TextField
                          label="Sale Price"
                          value={sale.salePrice}
                          onChange={(e) => {
                            const updated = [...inputs.manualInputs.recentSales];
                            updated[idx] = { ...updated[idx], salePrice: e.target.value };
                            setInputs(prev => ({
                              ...prev,
                              manualInputs: {
                                ...prev.manualInputs,
                                recentSales: updated
                              }
                            }));
                          }}
                          size="small"
                        />
                        <TextField
                          label="DOM"
                          value={sale.dom}
                          onChange={(e) => {
                            const updated = [...inputs.manualInputs.recentSales];
                            updated[idx] = { ...updated[idx], dom: e.target.value };
                            setInputs(prev => ({
                              ...prev,
                              manualInputs: {
                                ...prev.manualInputs,
                                recentSales: updated
                              }
                            }));
                          }}
                          size="small"
                        />
                        <IconButton
                          onClick={() => {
                            const updated = inputs.manualInputs.recentSales.filter((_, i) => i !== idx);
                            setInputs(prev => ({
                              ...prev,
                              manualInputs: {
                                ...prev.manualInputs,
                                recentSales: updated
                              }
                            }));
                          }}
                          size="small"
                        >
                          ×
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                  <Button
                    size="small"
                    onClick={() => addArrayItem('recentSales', 'recentSales', { address: '', salePrice: '', dom: '' })}
                    sx={{ mt: 1 }}
                  >
                    + Add Recent Sale
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Manual Inputs - Next Steps */}
          <Accordion expanded={expandedSections.includes('nextSteps')} onChange={() => handleSectionToggle('nextSteps')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Edit color="primary" />
                <Typography variant="subtitle1" sx={{ flex: 1 }}>
                  Next Steps Checklist
                </Typography>
                <Chip label="Editable" size="small" color="primary" variant="outlined" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {inputs.manualInputs.nextSteps.map((step, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      value={step}
                      onChange={(e) => {
                        const updated = [...inputs.manualInputs.nextSteps];
                        updated[idx] = e.target.value;
                        setInputs(prev => ({
                          ...prev,
                          manualInputs: {
                            ...prev.manualInputs,
                            nextSteps: updated
                          }
                        }));
                      }}
                      fullWidth
                      size="small"
                    />
                    <IconButton
                      onClick={() => {
                        const updated = inputs.manualInputs.nextSteps.filter((_, i) => i !== idx);
                        setInputs(prev => ({
                          ...prev,
                          manualInputs: {
                            ...prev.manualInputs,
                            nextSteps: updated
                          }
                        }));
                      }}
                      size="small"
                    >
                      ×
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  onClick={() => {
                    setInputs(prev => ({
                      ...prev,
                      manualInputs: {
                        ...prev.manualInputs,
                        nextSteps: [...prev.manualInputs.nextSteps, '']
                      }
                    }));
                  }}
                  sx={{ mt: 1 }}
                >
                  + Add Next Step
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>

        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading || saving}>
          Cancel
        </Button>
        {effectiveAgencySlug && effectivePropertyId && (
          <Button
            onClick={async () => {
              // Save current inputs first (so they're available for preview)
              if (onSave) {
                setSaving(true);
                try {
                  await onSave(inputs);
                } catch (err) {
                  console.error('Error saving before preview:', err);
                  // Still show preview even if save fails
                } finally {
                  setSaving(false);
                }
              }
              
              setPreviewLoading(true);
              setPreviewError(null);
              setPreviewHtml(null);
              setPreviewModalOpen(true);
              try {
                const { data } = await api.post(
                  `/agencies/${effectiveAgencySlug}/properties/${effectivePropertyId}/consultation-report/preview`,
                  { structuredInputs: inputs },
                  { responseType: 'text', headers: { Accept: 'text/html', 'Content-Type': 'application/json' } }
                );
                setPreviewHtml(typeof data === 'string' ? data : '');
              } catch (err: any) {
                setPreviewError(err.response?.data?.error || err.message || 'Failed to load preview.');
              } finally {
                setPreviewLoading(false);
              }
            }}
            variant="outlined"
            startIcon={<Preview />}
            disabled={loading || saving}
          >
            {saving ? 'Saving...' : 'Preview'}
          </Button>
        )}
        {onSave && (
          <Button
            onClick={async () => {
              setSaving(true);
              try {
                await onSave(inputs);
              } catch (err) {
                console.error('Error saving inputs:', err);
              } finally {
                setSaving(false);
              }
            }}
            variant="outlined"
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            disabled={loading || saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        )}
        <Button
          onClick={() => onConfirm(inputs)}
          variant="contained"
          color="primary"
          disabled={loading || saving}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
        >
          {loading ? 'Generating PDF...' : 'Confirm & Generate PDF'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Preview modal: HTML review only, no app menu — fetch HTML and show via srcDoc */}
    <Dialog
      open={previewModalOpen}
      onClose={() => {
        setPreviewModalOpen(false);
        setPreviewHtml(null);
        setPreviewError(null);
      }}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: 900,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Market Appraisal Preview</span>
        <IconButton
          aria-label="close preview"
          onClick={() => {
            setPreviewModalOpen(false);
            setPreviewHtml(null);
            setPreviewError(null);
          }}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {previewLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 300 }}>
            <CircularProgress />
          </Box>
        )}
        {previewError && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setPreviewError(null)}>
            {previewError}
          </Alert>
        )}
        {!previewLoading && previewHtml && (
          <iframe
            title="Market Appraisal Preview"
            srcDoc={previewHtml}
            style={{
              width: '100%',
              height: '100%',
              minHeight: 500,
              border: 'none',
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  </>
  );
};

export default MarketAppraisalConfirmDialog;
